import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Building2, Archive, ArchiveRestore, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const STATUS_COLORS = {
  attivo: "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800",
  sospeso: "bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800",
  chiuso: "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-700",
  archiviato: "bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800",
};

const EMPTY = {
  code: "", nome: "", comune: "", indirizzo: "", committente: "", impresa: "",
  progettista: "", direttore_lavori: "", direttore_operativo: "", responsabile: "",
  data_inizio: "", data_fine_prevista: "", stato: "attivo", note: "", image_url: "",
};

export default function Cantieri() {
  const { groupId } = useParams();
  const [search, setSearch] = useState("");
  const [stato, setStato] = useState("all");
  const [includeArchived, setIncludeArchived] = useState(false);
  // Cache DISTINTO per ogni combinazione di filtri: evita che cambi di stato/archivio
  // NON aggiornino la lista quando il cache dei filtri precedenti è ancora "fresco".
  // SAFE-BY-DESIGN: qui salviamo SOLO rows/total/offset/search (dati già filtrati dal
  // backend per group/utente). Nessun token/JWT/PII/segreto viene mai serializzato qui.
  const cacheKey = `cantieri_cache_${groupId || 'all'}_s=${stato}_a=${includeArchived ? 1 : 0}`;
  const cached = (() => { try { return JSON.parse(sessionStorage.getItem(cacheKey) || 'null'); } catch { return null; } })();
  const [rows, setRows] = useState(cached?.rows || []);
  const [total, setTotal] = useState(cached?.total ?? null);
  const [loading, setLoading] = useState(!cached);
  const [pageSize] = useState(50);
  const [offset, setOffset] = useState(cached?.offset || 0);
  const [hasMore, setHasMore] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = user && (user.role === "ADMIN" || user.role === "TECNICO");

  const load = async (opts = {}) => {
    const off = opts.append ? offset : 0;
    if (!opts.silent) setLoading(true);
    try {
      const params = { group_id: groupId, archived_only: includeArchived, limit: pageSize, offset: off };
      if (search) params.search = search;
      if (stato && stato !== "all") params.stato = stato;
      const { data } = await api.get("/cantieri", { params });
      const next = opts.append ? [...rows, ...data] : data;
      setRows(next);
      setOffset(off + data.length);
      setHasMore(data.length === pageSize);
      // aggiorna cache
      try { sessionStorage.setItem(cacheKey, JSON.stringify({ rows: next, total: next.length, offset: off + data.length, search, ts: Date.now() })); } catch (e) { void e; }
    } finally { setLoading(false); }
  };
  // primo load: se ho cache valida (< 60s) per QUESTA combinazione di filtri, ripristinala
  useEffect(() => {
    const c = (() => { try { return JSON.parse(sessionStorage.getItem(cacheKey) || 'null'); } catch { return null; } })();
    if (c && (Date.now() - (c.ts || 0)) < 60000) {
      setRows(c.rows || []); setTotal(c.total ?? null); setOffset(c.offset || 0); setLoading(false); return;
    }
    load();
  }, [groupId, stato, includeArchived]);
  useEffect(() => { const t = setTimeout(() => load(), 300); return () => clearTimeout(t); }, [search]);

  const invalidateCache = () => {
    try {
      const prefix = `cantieri_cache_${groupId || 'all'}`;
      Object.keys(sessionStorage).forEach(k => { if (k.startsWith(prefix)) sessionStorage.removeItem(k); });
    } catch (e) { void e; }
  };

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r.id);
    setForm({ ...EMPTY, ...r,
      data_inizio: r.data_inizio || "", data_fine_prevista: r.data_fine_prevista || "" });
    setOpen(true);
  };
  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    Object.keys(payload).forEach(k => { if (payload[k] === "") payload[k] = null; });
    payload.code = form.code;
    payload.nome = form.nome;
    payload.stato = form.stato;
    try {
      if (editing) {
        await api.patch(`/cantieri/${editing}`, payload);
        toast.success("Cantiere aggiornato");
      } else {
        await api.post("/cantieri", { ...payload, group_id: groupId });
        toast.success("Cantiere creato");
      }
      setOpen(false); invalidateCache(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const archive = async (id) => {
    if (!confirm("Archiviare il cantiere? Non sarà più visibile ma i dati resteranno nel database.")) return;
    try { await api.post(`/cantieri/${id}/archive`); toast.success("Archiviato"); invalidateCache(); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const restore = async (id) => {
    try { await api.post(`/cantieri/${id}/restore`); toast.success("Ripristinato"); invalidateCache(); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const deletePermanent = async (r) => {
    const msg = `ATTENZIONE: eliminerai definitivamente il cantiere "${r.nome}" (${r.code}) e TUTTI i dati collegati (computo, righe, dettagli, materiali, ordini, manodopera, ticket, note, allegati, permessi, SAL).\n\nQuesta azione NON è annullabile. Confermi?`;
    if (!confirm(msg)) return;
    const confirmCode = window.prompt(`Per confermare digita il codice del cantiere: ${r.code}`);
    if (confirmCode !== r.code) { toast.error("Codice non corrispondente: eliminazione annullata"); return; }
    try {
      await api.delete(`/cantieri/${r.id}/permanent`);
      toast.success(`Cantiere "${r.nome}" eliminato definitivamente`);
      invalidateCache(); await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore eliminazione");
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-amber-600 dark:text-amber-500 font-semibold">Anagrafica</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Cantieri</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-2">Gestisci cantieri, dati anagrafici e stato operativo.</p>
        </div>
        {canEdit && (
          <Button onClick={openNew} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold" data-testid="new-site-create-button">
            <Plus className="h-4 w-4 mr-2" /> Nuovo cantiere
          </Button>
        )}
      </div>

      <Card className="mb-4">
        <CardContent className="p-4 flex flex-col md:flex-row gap-3 md:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Cerca nome, codice, comune, committente, impresa…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="site-search-input" />
          </div>
          <Select value={stato} onValueChange={setStato}>
            <SelectTrigger className="w-full md:w-48" data-testid="site-status-filter-select"><SelectValue placeholder="Stato" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="attivo">Attivo</SelectItem>
              <SelectItem value="sospeso">Sospeso</SelectItem>
              <SelectItem value="chiuso">Chiuso</SelectItem>
              <SelectItem value="archiviato">Archiviato</SelectItem>
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
            <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} data-testid="show-archived-checkbox" />
            Mostra archiviati
          </label>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">Codice</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead className="hidden md:table-cell">Comune</TableHead>
                  <TableHead className="hidden lg:table-cell max-w-xs truncate">Indirizzo</TableHead>
                  <TableHead className="hidden lg:table-cell">Committente</TableHead>
                  <TableHead className="w-28">Stato</TableHead>
                  <TableHead className="w-36 text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-10">Caricamento…</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10">
                    <Building2 className="h-10 w-10 mx-auto text-slate-400 mb-2" />
                    <p className="text-slate-500">Nessun cantiere trovato</p>
                  </TableCell></TableRow>
                ) : rows.map(r => (
                  <TableRow key={r.id} data-testid={`site-table-row-${r.id}`} className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50" onClick={() => navigate(`/g/${groupId}/cantieri/${r.id}`)}>
                    <TableCell className="font-mono text-xs">{r.code}</TableCell>
                    <TableCell className="font-semibold">{r.nome}</TableCell>
                    <TableCell className="hidden md:table-cell">{r.comune || "—"}</TableCell>
                    <TableCell className="hidden lg:table-cell max-w-xs truncate" title={r.indirizzo || ""}>{r.indirizzo || "—"}</TableCell>
                    <TableCell className="hidden lg:table-cell">{r.committente || "—"}</TableCell>
                    <TableCell><Badge variant="outline" className={`capitalize ${STATUS_COLORS[r.stato]}`}>{r.stato}</Badge></TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      {canEdit && (
                        <Button size="icon" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-site-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                      )}
                      {user?.role === "ADMIN" && !r.is_archived && (
                        <Button size="icon" variant="ghost" onClick={() => archive(r.id)} data-testid={`archive-site-${r.id}`}><Archive className="h-4 w-4" /></Button>
                      )}
                      {user?.role === "ADMIN" && r.is_archived && (
                        <Button size="icon" variant="ghost" onClick={() => restore(r.id)} data-testid={`restore-site-${r.id}`}><ArchiveRestore className="h-4 w-4" /></Button>
                      )}
                      {user?.role === "ADMIN" && r.is_archived && (
                        <Button size="icon" variant="ghost" onClick={() => deletePermanent(r)} data-testid={`delete-permanent-site-${r.id}`} title="Elimina definitivamente"><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {hasMore && rows.length >= pageSize && (
              <div className="p-3 text-center border-t border-slate-200 dark:border-slate-800">
                <Button size="sm" variant="outline" onClick={() => load({ append: true })} disabled={loading} data-testid="cantieri-load-more">
                  {loading ? "Caricamento…" : `Carica altri (mostrati ${rows.length})`}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica cantiere" : "Nuovo cantiere"}</DialogTitle><DialogDescription>Compila i dati anagrafici del cantiere.</DialogDescription></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              ["code", "Codice cantiere *", "text", true],
              ["nome", "Nome cantiere *", "text", true],
              ["comune", "Comune"],
              ["indirizzo", "Indirizzo"],
              ["committente", "Committente"],
              ["impresa", "Impresa"],
              ["progettista", "Progettista"],
              ["direttore_lavori", "Direttore lavori"],
              ["direttore_operativo", "Direttore operativo"],
              ["responsabile", "Responsabile"],
              ["data_inizio", "Data inizio", "date"],
              ["data_fine_prevista", "Data fine prevista", "date"],
            ].map(([k, label, type = "text", req = false]) => (
              <div key={k}>
                <Label htmlFor={k}>{label}</Label>
                <Input id={k} type={type} required={req} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`site-form-${k}`} />
              </div>
            ))}
            <div>
              <Label htmlFor="stato">Stato</Label>
              <Select value={form.stato} onValueChange={(v) => setForm({ ...form, stato: v })}>
                <SelectTrigger id="stato" data-testid="site-form-stato"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="attivo">Attivo</SelectItem>
                  <SelectItem value="sospeso">Sospeso</SelectItem>
                  <SelectItem value="chiuso">Chiuso</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="image_url">Immagine (URL)</Label>
              <Input id="image_url" value={form.image_url ?? ""} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="site-form-image_url" />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="note">Note</Label>
              <Textarea id="note" value={form.note ?? ""} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="site-form-note" />
            </div>
            <DialogFooter className="md:col-span-2">
              <Button type="submit" data-testid="site-form-submit" className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold">{editing ? "Salva" : "Crea"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
